import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  SimpleGrid,
  Badge,
  Icon,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  Tooltip as ChakraTooltip,
  Grid,
  GridItem,
  Circle,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  Cell,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';
import {
  FaChartLine,
  FaSearch,
  FaGlobe,
  FaStar,
  FaTrophy,
  FaArrowUp,
  FaArrowDown,
  FaChartBar,
  FaUsers,
  FaThumbsUp,
  FaCrown,
  FaMedal,
  FaAward,
  FaBalanceScale,
} from 'react-icons/fa';
import { GiStrongMan } from 'react-icons/gi';
import { MdOutlineSick } from 'react-icons/md';
import ReactMarkdown from 'react-markdown';
import getYearData from '../services/yearService.js';
import { getHistoricalTraffic, getReviews } from '../services/reviewService.js';
import { getBusinessImages } from '../services/imageService.js';
import { logger } from '../utils/logger';

// Color palette for businesses - distinct, high-contrast colors for clear differentiation
const BUSINESS_COLORS = [
  '#2563EB', // vivid blue
  '#DC2626', // crimson red
  '#059669', // emerald green
  '#EA580C', // bright orange
  '#7C3AED', // rich purple
  '#DB2777', // hot pink
  '#0891B2', // cyan/teal
  '#CA8A04', // golden yellow
  '#475569', // slate gray
  '#65A30D', // lime green
];

// Sentiment colors
const SENTIMENT_COLORS = {
  positive: '#10B981',
  neutral: '#F59E0B',
  negative: '#EF4444',
};

// SWOT section colors
const SWOT_COLORS = {
  strengths: { bg: 'green.50', border: 'green.200', heading: 'green.600', darkBg: 'green.900', darkBorder: 'green.700' },
  weaknesses: { bg: 'red.50', border: 'red.200', heading: 'red.600', darkBg: 'red.900', darkBorder: 'red.700' },
};

/**
 * Parse SWOT sections from versus_insights content
 * @param {string} content - The versus_insights markdown content
 * @returns {Object} - Object with strengths and weaknesses sections
 */
const parseSwotSections = (content) => {
  if (!content) return { strengths: '', weaknesses: '' };

  const sections = {
    strengths: '',
    weaknesses: '',
  };

  // Improved regex patterns that can handle markdown formatting with ** asterisks
  const strengthsRegex = /\*\*\s*strengths:?\s*\*\*/i;
  const weaknessesRegex = /\*\*\s*weaknesses:?\s*\*\*/i;
  const opportunitiesRegex = /\*\*\s*opportunities:?\s*\*\*/i;

  // Try to find section positions
  const strengthsMatch = content.match(strengthsRegex);
  const weaknessesMatch = content.match(weaknessesRegex);
  const opportunitiesMatch = content.match(opportunitiesRegex);

  if (strengthsMatch && weaknessesMatch) {
    const strengthsPos = strengthsMatch.index;
    const weaknessesPos = weaknessesMatch.index;
    const opportunitiesPos = opportunitiesMatch ? opportunitiesMatch.index : content.length;

    // Extract strengths (between strengths header and weaknesses header)
    sections.strengths = content
      .substring(strengthsPos + strengthsMatch[0].length, weaknessesPos)
      .trim();

    // Extract weaknesses (between weaknesses header and opportunities header or end)
    sections.weaknesses = content
      .substring(weaknessesPos + weaknessesMatch[0].length, opportunitiesPos)
      .trim();

    return sections;
  }

  // Try plain text format
  const plainStrengthsRegex = /strengths:?(?:\s*\n|\s*$)/i;
  const plainWeaknessesRegex = /weaknesses:?(?:\s*\n|\s*$)/i;
  const plainOpportunitiesRegex = /opportunities:?(?:\s*\n|\s*$)/i;

  const plainStrengthsMatch = content.match(plainStrengthsRegex);
  const plainWeaknessesMatch = content.match(plainWeaknessesRegex);
  const plainOpportunitiesMatch = content.match(plainOpportunitiesRegex);

  if (plainStrengthsMatch && plainWeaknessesMatch) {
    const strengthsPos = plainStrengthsMatch.index;
    const weaknessesPos = plainWeaknessesMatch.index;
    const opportunitiesPos = plainOpportunitiesMatch ? plainOpportunitiesMatch.index : content.length;

    sections.strengths = content
      .substring(strengthsPos + plainStrengthsMatch[0].length, weaknessesPos)
      .trim();

    sections.weaknesses = content
      .substring(weaknessesPos + plainWeaknessesMatch[0].length, opportunitiesPos)
      .trim();

    return sections;
  }

  // Try line-based parsing
  const lines = content.split('\n');
  let currentSection = null;

  for (const line of lines) {
    const trimmedLine = line.trim();
    const lowerLine = trimmedLine.toLowerCase();

    if (
      trimmedLine.match(/\*\*\s*strengths:?\s*\*\*/i) ||
      lowerLine === '**strengths**' ||
      lowerLine === 'strengths:'
    ) {
      currentSection = 'strengths';
      continue;
    } else if (
      trimmedLine.match(/\*\*\s*weaknesses:?\s*\*\*/i) ||
      lowerLine === '**weaknesses**' ||
      lowerLine === 'weaknesses:'
    ) {
      currentSection = 'weaknesses';
      continue;
    } else if (
      trimmedLine.match(/\*\*\s*opportunities:?\s*\*\*/i) ||
      lowerLine === '**opportunities**' ||
      lowerLine === 'opportunities:'
    ) {
      currentSection = null; // Stop collecting
      continue;
    }

    if (currentSection && trimmedLine) {
      sections[currentSection] += line + '\n';
    }
  }

  return {
    strengths: sections.strengths.trim(),
    weaknesses: sections.weaknesses.trim(),
  };
};

// Markdown components for SWOT content rendering
const swotMarkdownComponents = {
  p: (props) => (
    <Text fontSize="sm" color="gray.600" mb={2} lineHeight="1.6" {...props} />
  ),
  strong: (props) => (
    <Text as="span" fontWeight="bold" {...props} />
  ),
  em: (props) => (
    <Text as="span" fontStyle="italic" {...props} />
  ),
  ul: ({ children, ...props }) => (
    <Box as="ul" pl={4} mb={2} {...props}>
      {children}
    </Box>
  ),
  li: ({ children, ...props }) => (
    <Box as="li" mb={1} fontSize="sm" color="gray.600" {...props}>
      {children}
    </Box>
  ),
};

const CompareBusinessDashboard = () => {
  const [yearData, setYearData] = useState([]);
  const [historicalData, setHistoricalData] = useState(null);
  const [reviewsData, setReviewsData] = useState([]);
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [businessColors, setBusinessColors] = useState({});
  const [businessImages, setBusinessImages] = useState({});

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const accentColor = useColorModeValue('blue.500', 'blue.400');
  
  // SWOT section color mode values
  const strengthsBg = useColorModeValue(SWOT_COLORS.strengths.bg, SWOT_COLORS.strengths.darkBg);
  const strengthsBorder = useColorModeValue(SWOT_COLORS.strengths.border, SWOT_COLORS.strengths.darkBorder);
  const weaknessesBg = useColorModeValue(SWOT_COLORS.weaknesses.bg, SWOT_COLORS.weaknesses.darkBg);
  const weaknessesBorder = useColorModeValue(SWOT_COLORS.weaknesses.border, SWOT_COLORS.weaknesses.darkBorder);

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [yearResponse, trafficResponse, reviewsResponse] = await Promise.all([
          getYearData(),
          getHistoricalTraffic(),
          getReviews(),
        ]);

        setYearData(yearResponse || []);
        setHistoricalData(trafficResponse || { businesses: [] });
        setReviewsData(reviewsResponse || []);

        // Set default selected businesses (top 3 by reviews)
        if (yearResponse && yearResponse.length > 0) {
          const sortedByReviews = [...yearResponse].sort(
            (a, b) => (b.data?.totalReviews || 0) - (a.data?.totalReviews || 0)
          );
          const defaultSelected = sortedByReviews.slice(0, 3).map((b) => b.name);
          setSelectedBusinesses(defaultSelected);

          // Assign colors to all businesses
          const colors = {};
          yearResponse.forEach((business, index) => {
            colors[business.name] = BUSINESS_COLORS[index % BUSINESS_COLORS.length];
          });
          setBusinessColors(colors);
        }
      } catch (error) {
        logger.error('Error fetching comparison data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch business images
  useEffect(() => {
    let mounted = true;
    
    const fetchImages = async () => {
      try {
        const images = await getBusinessImages();
        if (mounted && images) {
          const imageMap = {};
          images.forEach(img => {
            if (img.maps_uri && img.high_res_image_url_fetched) {
              imageMap[img.maps_uri] = img.high_res_image_url_fetched;
            }
          });
          setBusinessImages(imageMap);
        }
      } catch (error) {
        logger.error('Error fetching business images:', error);
      }
    };

    fetchImages();
    
    return () => {
      mounted = false;
    };
  }, []);

  // Get color for a business based on its position in selected businesses
  // This ensures each selected business gets a distinct color
  const getColor = useCallback(
    (businessName) => {
      const selectedIndex = selectedBusinesses.indexOf(businessName);
      if (selectedIndex >= 0) {
        return BUSINESS_COLORS[selectedIndex % BUSINESS_COLORS.length];
      }
      return businessColors[businessName] || BUSINESS_COLORS[0];
    },
    [businessColors, selectedBusinesses]
  );

  // Business select options
  const selectOptions = useMemo(() => {
    if (!yearData.length) return [];

    return yearData
      .sort((a, b) => (b.data?.totalReviews || 0) - (a.data?.totalReviews || 0))
      .map((business) => ({
        value: business.name,
        label: `${business.name} (${business.data?.totalReviews || 0} reviews)`,
      }));
  }, [yearData]);

  // Handle business selection
  const handleBusinessSelection = (selectedOptions) => {
    setSelectedBusinesses(selectedOptions.map((option) => option.value));
  };

  // Get selected businesses data from yearData
  const selectedBusinessesData = useMemo(() => {
    return yearData.filter((business) =>
      selectedBusinesses.includes(business.name)
    );
  }, [yearData, selectedBusinesses]);

  // Calculate rankings among selected businesses
  const businessRankings = useMemo(() => {
    if (!selectedBusinessesData.length) return {};

    const rankings = {};
    const metrics = ['totalReviews', 'averageRating'];
    
    metrics.forEach((metric) => {
      const sorted = [...selectedBusinessesData].sort((a, b) => {
        const aVal = a.data?.[metric] || 0;
        const bVal = b.data?.[metric] || 0;
        return bVal - aVal;
      });
      sorted.forEach((business, index) => {
        if (!rankings[business.name]) rankings[business.name] = {};
        rankings[business.name][metric] = index + 1;
      });
    });

    // Sentiment ranking (by positive percentage)
    const sentimentSorted = [...selectedBusinessesData].sort((a, b) => {
      const aS = a.data?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const bS = b.data?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
      const aTotal = aS.positive + aS.neutral + aS.negative;
      const bTotal = bS.positive + bS.neutral + bS.negative;
      const aPercent = aTotal > 0 ? aS.positive / aTotal : 0;
      const bPercent = bTotal > 0 ? bS.positive / bTotal : 0;
      return bPercent - aPercent;
    });
    sentimentSorted.forEach((business, index) => {
      if (!rankings[business.name]) rankings[business.name] = {};
      rankings[business.name].sentiment = index + 1;
    });

    return rankings;
  }, [selectedBusinessesData]);

  // Calculate review growth data for each business (last month vs previous month)
  const reviewGrowthData = useMemo(() => {
    const growthData = {};
    
    selectedBusinessesData.forEach((business) => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      const sortedMonths = Object.keys(monthlyData).sort((a, b) => new Date(b) - new Date(a));
      
      if (sortedMonths.length >= 1) {
        const lastMonth = sortedMonths[0];
        const lastMonthReviews = monthlyData[lastMonth] || 0;
        const previousMonthReviews = sortedMonths.length >= 2 ? (monthlyData[sortedMonths[1]] || 0) : 0;
        
        let growthPercent = 0;
        if (previousMonthReviews > 0) {
          growthPercent = ((lastMonthReviews - previousMonthReviews) / previousMonthReviews) * 100;
        } else if (lastMonthReviews > 0) {
          growthPercent = 100; // New reviews from 0
        }
        
        growthData[business.name] = {
          lastMonth: lastMonth,
          lastMonthReviews: lastMonthReviews,
          previousMonthReviews: previousMonthReviews,
          change: lastMonthReviews - previousMonthReviews,
          growthPercent: growthPercent,
          isPositive: lastMonthReviews >= previousMonthReviews,
        };
      } else {
        growthData[business.name] = {
          lastMonth: null,
          lastMonthReviews: 0,
          previousMonthReviews: 0,
          change: 0,
          growthPercent: 0,
          isPositive: true,
        };
      }
    });
    
    return growthData;
  }, [selectedBusinessesData]);

  // Reviews Over Time Data (Line Chart)
  const reviewsOverTimeData = useMemo(() => {
    if (!selectedBusinessesData.length) return [];

    const allMonths = new Set();
    selectedBusinessesData.forEach((business) => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      Object.keys(monthlyData).forEach((month) => allMonths.add(month));
    });

    const sortedMonths = Array.from(allMonths).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    return sortedMonths.map((month) => {
      const dataPoint = { month };
      selectedBusinessesData.forEach((business) => {
        dataPoint[business.name] =
          business.data?.monthlyReviewCounts?.[month] || 0;
      });
      return dataPoint;
    });
  }, [selectedBusinessesData]);

  // Total Reviews Over Time Data (Cumulative - lines only go up)
  const totalReviewsOverTimeData = useMemo(() => {
    if (!selectedBusinessesData.length) return [];

    const allMonths = new Set();
    selectedBusinessesData.forEach((business) => {
      const monthlyData = business.data?.monthlyReviewCounts || {};
      Object.keys(monthlyData).forEach((month) => allMonths.add(month));
    });

    const sortedMonths = Array.from(allMonths).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    if (sortedMonths.length === 0) return [];

    // Calculate starting total for each business
    // Use actual current rating_count from reviewsData, then subtract reviews gained in the range
    const startingTotals = {};
    selectedBusinessesData.forEach((business) => {
      // Find matching business in reviewsData to get actual current rating_count
      const currentData = reviewsData.find(
        (r) => r.maps_uri === business.maps_uri || r.name === business.name
      );
      const currentTotal = currentData?.rating_count || business.data?.totalReviews || 0;
      const monthlyData = business.data?.monthlyReviewCounts || {};
      
      // Sum reviews from all months in range
      let reviewsInRange = 0;
      sortedMonths.forEach((month) => {
        reviewsInRange += monthlyData[month] || 0;
      });
      
      // Starting total is current total minus all reviews in the range
      // This gives us the review count at the START of the tracked period
      startingTotals[business.name] = Math.max(0, currentTotal - reviewsInRange);
    });

    // Track running totals for each business
    const runningTotals = { ...startingTotals };

    // Create data points for each month with CUMULATIVE total reviews
    return sortedMonths.map((month) => {
      const dataPoint = { month };
      
      selectedBusinessesData.forEach((business) => {
        const monthlyData = business.data?.monthlyReviewCounts || {};
        // Add this month's new reviews to the running total
        runningTotals[business.name] += monthlyData[month] || 0;
        dataPoint[business.name] = runningTotals[business.name];
      });

      return dataPoint;
    });
  }, [selectedBusinessesData, reviewsData]);

  // Customer Sentiment Data
  const sentimentData = useMemo(() => {
    return selectedBusinessesData.map((business) => {
      const sentiment = business.data?.sentimentBreakdown || {
        positive: 0,
        neutral: 0,
        negative: 0,
      };
      const total = sentiment.positive + sentiment.neutral + sentiment.negative;
      return {
        name: business.name.length > 20 ? business.name.substring(0, 20) + '...' : business.name,
        fullName: business.name,
        positive: sentiment.positive,
        neutral: sentiment.neutral,
        negative: sentiment.negative,
        total,
        positivePercent: total > 0 ? ((sentiment.positive / total) * 100).toFixed(1) : 0,
        neutralPercent: total > 0 ? ((sentiment.neutral / total) * 100).toFixed(1) : 0,
        negativePercent: total > 0 ? ((sentiment.negative / total) * 100).toFixed(1) : 0,
      };
    });
  }, [selectedBusinessesData]);

  // Radar chart data for overall comparison
  const radarData = useMemo(() => {
    if (!selectedBusinessesData.length) return [];

    const maxReviews = Math.max(...selectedBusinessesData.map(b => b.data?.totalReviews || 0));

    const metrics = [
      { metric: 'Reviews', key: 'reviews' },
      { metric: 'Rating', key: 'rating' },
      { metric: 'Sentiment', key: 'sentiment' },
      { metric: 'Growth', key: 'growth' },
      { metric: 'Consistency', key: 'consistency' },
    ];

    return metrics.map((m) => {
      const dataPoint = { metric: m.metric };
      selectedBusinessesData.forEach((business) => {
        let value = 0;
        const sentiment = business.data?.sentimentBreakdown || { positive: 0, neutral: 0, negative: 0 };
        const sentTotal = sentiment.positive + sentiment.neutral + sentiment.negative;
        const monthly = Object.values(business.data?.monthlyReviewCounts || {});

        switch (m.key) {
          case 'reviews':
            value = maxReviews > 0 ? ((business.data?.totalReviews || 0) / maxReviews) * 100 : 0;
            break;
          case 'rating':
            value = ((business.data?.averageRating || 0) / 5) * 100;
            break;
          case 'sentiment':
            value = sentTotal > 0 ? (sentiment.positive / sentTotal) * 100 : 0;
            break;
          case 'growth':
            if (monthly.length >= 2) {
              const recent = monthly.slice(-3).reduce((a, b) => a + b, 0) / 3;
              const older = monthly.slice(0, 3).reduce((a, b) => a + b, 0) / 3;
              value = older > 0 ? Math.min(((recent / older) * 50), 100) : 50;
            } else {
              value = 50;
            }
            break;
          case 'consistency':
            if (monthly.length > 0) {
              const avg = monthly.reduce((a, b) => a + b, 0) / monthly.length;
              const variance = monthly.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / monthly.length;
              const stdDev = Math.sqrt(variance);
              const cv = avg > 0 ? stdDev / avg : 0;
              value = Math.max(0, 100 - (cv * 100));
            }
            break;
          default:
            value = 0;
        }
        dataPoint[business.name] = Math.round(value);
      });
      return dataPoint;
    });
  }, [selectedBusinessesData]);

  // SEO Data
  const seoData = useMemo(() => {
    if (!historicalData?.businesses?.length) return [];

    return selectedBusinesses
      .map((businessName) => {
        const historicalBusiness = historicalData.businesses.find(
          (b) => b.name === businessName
        );

        if (!historicalBusiness?.timeSeries?.length) {
          return null;
        }

        const latestData =
          historicalBusiness.timeSeries[historicalBusiness.timeSeries.length - 1];

        return {
          name: businessName.length > 15 ? businessName.substring(0, 15) + '...' : businessName,
          fullName: businessName,
          domainStrength: latestData?.strength || 0,
          organicValue: latestData?.organicValue || 0,
          organicClicks: latestData?.organicClicks || 0,
          fill: getColor(businessName),
        };
      })
      .filter(Boolean);
  }, [selectedBusinesses, historicalData, getColor]);

  // SWOT Comparison Data - extract strengths and weaknesses for selected businesses
  const swotComparisonData = useMemo(() => {
    if (!selectedBusinesses.length || !reviewsData.length) return [];

    return selectedBusinesses
      .map((businessName) => {
        // Find the business in reviewsData by name
        const businessData = reviewsData.find(
          (b) => b.name === businessName
        );

        if (!businessData?.versus_insights) {
          return null;
        }

        const swotSections = parseSwotSections(businessData.versus_insights);

        // Only include businesses that have at least one SWOT section
        if (!swotSections.strengths && !swotSections.weaknesses) {
          return null;
        }

        return {
          name: businessName,
          shortName: businessName.length > 25 ? businessName.substring(0, 25) + '...' : businessName,
          strengths: swotSections.strengths,
          weaknesses: swotSections.weaknesses,
          color: getColor(businessName),
        };
      })
      .filter(Boolean);
  }, [selectedBusinesses, reviewsData, getColor]);

  // Website Traffic Over Time Data
  const trafficOverTimeData = useMemo(() => {
    if (!historicalData?.businesses?.length) return [];

    const selectedHistorical = historicalData.businesses.filter((b) =>
      selectedBusinesses.includes(b.name)
    );

    if (!selectedHistorical.length) return [];

    const allMonths = new Set();
    selectedHistorical.forEach((business) => {
      business.timeSeries?.forEach((ts) => {
        allMonths.add(ts.yearMonth);
      });
    });

    const sortedMonths = Array.from(allMonths)
      .sort()
      .slice(-12);

    return sortedMonths.map((month) => {
      const dataPoint = { month };
      selectedHistorical.forEach((business) => {
        const monthData = business.timeSeries?.find(
          (ts) => ts.yearMonth === month
        );
        dataPoint[business.name] = monthData?.organicClicks || 0;
      });
      return dataPoint;
    });
  }, [selectedBusinesses, historicalData]);

  // Format month for display
  const formatMonth = (dateString) => {
    const date = new Date(dateString + '-01T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  // Get rank icon
  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return FaCrown;
      case 2:
        return FaMedal;
      case 3:
        return FaAward;
      default:
        return null;
    }
  };

  // Get rank color
  const getRankColor = (rank) => {
    switch (rank) {
      case 1:
        return 'yellow.400';
      case 2:
        return 'gray.400';
      case 3:
        return 'orange.400';
      default:
        return 'gray.300';
    }
  };

  if (isLoading) {
    return (
      <Flex height="100vh" alignItems="center" justifyContent="center">
        <VStack spacing={4}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
          <Text fontSize="xl" fontWeight="bold">
            Loading Comparison Data...
          </Text>
        </VStack>
      </Flex>
    );
  }

  if (!yearData.length) {
    return (
      <Box w="100%" padding={6}>
        <HStack spacing={2} align="center" mb={4}>
          <Box
            bgGradient="linear(to-r, blue.500, purple.500)"
            p={2}
            borderRadius="lg"
          >
            <Icon as={FaBalanceScale} color="white" boxSize={5} />
          </Box>
          <Heading 
            size="lg" 
            bgGradient="linear(to-r, blue.600, purple.600)"
            bgClip="text"
            fontWeight="bold"
          >
            Compare Businesses
          </Heading>
        </HStack>
        <Text color="gray.500">No business data available for comparison.</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      {/* Header */}
      <Flex align="center" justify="space-between" mb={6} flexWrap="wrap" gap={4}>
        <HStack spacing={2} align="center">
          <Box
            bgGradient="linear(to-r, blue.500, purple.500)"
            p={2}
            borderRadius="lg"
          >
            <Icon as={FaBalanceScale} color="white" boxSize={5} />
          </Box>
          <Heading 
            size="lg" 
            bgGradient="linear(to-r, blue.600, purple.600)"
            bgClip="text"
            fontWeight="bold"
          >
            Compare Businesses
          </Heading>
          {selectedBusinesses.length > 0 && (
            <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
              {selectedBusinesses.length} selected
            </Badge>
          )}
        </HStack>
      </Flex>

      {/* Business Selection Card */}
      <Box
        bg={cardBg}
        borderRadius="xl"
        p={6}
        shadow="md"
        border="1px solid"
        borderColor={borderColor}
        mb={6}
      >
        <HStack spacing={3} mb={4} align="center">
          <Icon as={FaUsers} color={accentColor} boxSize={5} />
          <VStack align="start" spacing={0}>
            <Text fontWeight="semibold" color={textColor}>
              Select Businesses to Compare
            </Text>
            <Text fontSize="sm" color={subtitleColor}>
              Choose up to 5 businesses for optimal comparison
            </Text>
          </VStack>
        </HStack>
          
          <Select
            isMulti
            name="businesses"
            options={selectOptions}
            placeholder="Search and select businesses..."
            closeMenuOnSelect={false}
            onChange={handleBusinessSelection}
            value={selectOptions.filter((option) =>
              selectedBusinesses.includes(option.value)
            )}
            chakraStyles={{
              container: (provided) => ({
                ...provided,
                width: '100%',
              }),
              control: (provided) => ({
                ...provided,
                borderRadius: 'lg',
                borderColor: borderColor,
              }),
              dropdownIndicator: (provided) => ({
                ...provided,
                background: 'transparent',
                border: 'none',
              }),
              multiValue: (provided, state) => ({
                ...provided,
                backgroundColor: getColor(state.data.value),
                color: 'white',
                borderRadius: 'md',
              }),
              multiValueLabel: (provided) => ({
                ...provided,
                color: 'white',
              }),
              multiValueRemove: (provided) => ({
                ...provided,
                color: 'white',
                '&:hover': {
                  backgroundColor: 'whiteAlpha.300',
                  color: 'white',
                },
              }),
            }}
          />
        </Box>

        {selectedBusinesses.length === 0 ? (
          <Box
            bg={cardBg}
            borderRadius="lg"
            p={8}
            textAlign="center"
            border="1px solid"
            borderColor={borderColor}
          >
            <Icon as={FaChartBar} boxSize={12} color="gray.300" mb={4} />
            <Heading size="md" color={subtitleColor} mb={2}>
              No Businesses Selected
            </Heading>
            <Text color={subtitleColor}>
              Please select at least one business above to view comparison analytics.
            </Text>
          </Box>
        ) : (
          <VStack spacing={6} align="stretch">
            {/* Quick Stats Grid */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: selectedBusinesses.length }} spacing={4}>
              {selectedBusinessesData.map((business, index) => {
                const sentiment = business.data?.sentimentBreakdown || {
                  positive: 0,
                  neutral: 0,
                  negative: 0,
                };
                const total = sentiment.positive + sentiment.neutral + sentiment.negative;
                const positivePercent = total > 0 ? ((sentiment.positive / total) * 100).toFixed(0) : 0;
                const rank = businessRankings[business.name]?.totalReviews || 0;
                const RankIcon = getRankIcon(rank);
                const imageUrl = business.maps_uri ? businessImages[business.maps_uri] : null;

                return (
                  <Box
                    key={business.name}
                    bg={cardBg}
                    borderRadius="lg"
                    overflow="hidden"
                    shadow="md"
                    border="1px solid"
                    borderColor={borderColor}
                    position="relative"
                    transition="all 0.2s"
                    _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                  >
                    {/* Colored top bar */}
                    <Box
                      h="3px"
                      bg={getColor(business.name)}
                    />
                    
                    <Box p={5}>
                      <HStack justify="space-between" mb={4}>
                        <HStack spacing={3}>
                          <Avatar
                            size="md"
                            name={business.name}
                            src={imageUrl}
                            bg={getColor(business.name)}
                            color="white"
                          />
                          <VStack align="start" spacing={0}>
                            <Text
                              fontWeight="bold"
                              fontSize="md"
                              color={textColor}
                              noOfLines={1}
                              maxW="180px"
                            >
                              {business.name}
                            </Text>
                            <Flex align="center" gap={1}>
                              <Icon as={FaStar} color="yellow.400" boxSize="12px" position="relative" top="2px" />
                              <Text fontSize="sm" color={subtitleColor}>
                                {business.data?.averageRating?.toFixed(1) || 'N/A'}
                              </Text>
                            </Flex>
                          </VStack>
                        </HStack>
                        {RankIcon && (
                          <ChakraTooltip label={`#${rank} in reviews`} placement="top">
                            <Circle size="32px" bg={getRankColor(rank)} color="white">
                              <Icon as={RankIcon} boxSize={4} />
                            </Circle>
                          </ChakraTooltip>
                        )}
                      </HStack>

                      <SimpleGrid columns={2} spacing={3}>
                        <Stat size="sm">
                          <StatLabel fontSize="xs" color={subtitleColor}>
                            Total Reviews
                          </StatLabel>
                          <StatNumber fontSize="xl" color={textColor}>
                            {(business.data?.totalReviews || 0).toLocaleString()}
                          </StatNumber>
                        </Stat>
                        <Stat size="sm">
                          <StatLabel fontSize="xs" color={subtitleColor}>
                            Positive
                          </StatLabel>
                          <StatNumber fontSize="xl" color="green.500">
                            {positivePercent}%
                          </StatNumber>
                        </Stat>
                      </SimpleGrid>

                      {/* Sentiment Bar */}
                      <Box mt={4}>
                        <HStack spacing={1} h="8px" borderRadius="full" overflow="hidden">
                          <Box
                            bg={SENTIMENT_COLORS.positive}
                            h="full"
                            flex={sentiment.positive || 1}
                            transition="all 0.3s"
                          />
                          <Box
                            bg={SENTIMENT_COLORS.neutral}
                            h="full"
                            flex={sentiment.neutral || 1}
                            transition="all 0.3s"
                          />
                          <Box
                            bg={SENTIMENT_COLORS.negative}
                            h="full"
                            flex={sentiment.negative || 1}
                            transition="all 0.3s"
                          />
                        </HStack>
                        <HStack justify="space-between" mt={2}>
                          <HStack spacing={1}>
                            <Circle size="8px" bg={SENTIMENT_COLORS.positive} />
                            <Text fontSize="xs" color={subtitleColor}>
                              {sentiment.positive}
                            </Text>
                          </HStack>
                          <HStack spacing={1}>
                            <Circle size="8px" bg={SENTIMENT_COLORS.neutral} />
                            <Text fontSize="xs" color={subtitleColor}>
                              {sentiment.neutral}
                            </Text>
                          </HStack>
                          <HStack spacing={1}>
                            <Circle size="8px" bg={SENTIMENT_COLORS.negative} />
                            <Text fontSize="xs" color={subtitleColor}>
                              {sentiment.negative}
                            </Text>
                          </HStack>
                        </HStack>
                      </Box>

                      {/* Review Growth Last Month */}
                      {reviewGrowthData[business.name] && (
                        <Box
                          mt={3}
                          pt={3}
                          borderTop="1px solid"
                          borderColor={borderColor}
                        >
                          <Text fontSize="xs" color={subtitleColor} fontWeight="medium" mb={2}>
                            Review Growth Last Month
                            {reviewGrowthData[business.name].lastMonth && (
                              <Text as="span" fontWeight="normal"> · {new Date(reviewGrowthData[business.name].lastMonth).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</Text>
                            )}
                          </Text>
                          <HStack spacing={3}>
                            <HStack spacing={1}>
                              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                                +{reviewGrowthData[business.name].lastMonthReviews}
                              </Text>
                              <Text fontSize="xs" color={subtitleColor}>
                                reviews
                              </Text>
                            </HStack>
                            {reviewGrowthData[business.name].previousMonthReviews > 0 && (
                              <HStack
                                spacing={1}
                                px={2}
                                py={0.5}
                                borderRadius="full"
                                bg={reviewGrowthData[business.name].isPositive ? 'green.50' : 'red.50'}
                              >
                                <Icon
                                  as={reviewGrowthData[business.name].isPositive ? FaArrowUp : FaArrowDown}
                                  color={reviewGrowthData[business.name].isPositive ? 'green.500' : 'red.500'}
                                  boxSize={2.5}
                                />
                                <Text
                                  fontSize="xs"
                                  fontWeight="semibold"
                                  color={reviewGrowthData[business.name].isPositive ? 'green.600' : 'red.600'}
                                >
                                  {Math.abs(reviewGrowthData[business.name].growthPercent).toFixed(0)}%
                                </Text>
                              </HStack>
                            )}
                          </HStack>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </SimpleGrid>

            {/* Charts Grid */}
            <Grid
              templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
              gap={6}
            >
              {/* Reviews Over Time */}
              <GridItem colSpan={{ base: 1, lg: 2 }}>
                <Box
                  bg={cardBg}
                  borderRadius="lg"
                  p={6}
                  shadow="lg"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <HStack spacing={3} mb={6}>
                    <Circle size="40px" bg="blue.100" color="blue.600">
                      <Icon as={FaChartLine} boxSize={4} />
                    </Circle>
                    <VStack align="start" spacing={0}>
                      <Heading size="md" color={textColor}>
                        Reviews Over Time
                      </Heading>
                      <Text fontSize="sm" color={subtitleColor}>
                        Monthly review volume trends for selected businesses
                      </Text>
                    </VStack>
                  </HStack>
                  
                  <Box height="400px">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={reviewsOverTimeData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 40 }}
                      >
                        <defs>
                          {selectedBusinessesData.map((business) => (
                            <linearGradient
                              key={`gradient-${business.name}`}
                              id={`color-${business.name.replace(/\s/g, '')}`}
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor={getColor(business.name)}
                                stopOpacity={0.3}
                              />
                              <stop
                                offset="95%"
                                stopColor={getColor(business.name)}
                                stopOpacity={0}
                              />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis
                          dataKey="month"
                          tickFormatter={formatMonth}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                          tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                          axisLine={{ stroke: '#E2E8F0' }}
                        />
                        <YAxis
                          tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                          axisLine={{ stroke: '#E2E8F0' }}
                        />
                        <Tooltip
                          labelFormatter={formatMonth}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            padding: '12px 16px',
                          }}
                        />
                        <Legend
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="circle"
                        />
                        {selectedBusinessesData.map((business) => (
                          <Area
                            key={business.name}
                            type="monotone"
                            dataKey={business.name}
                            stroke={getColor(business.name)}
                            strokeWidth={2.5}
                            fill={`url(#color-${business.name.replace(/\s/g, '')})`}
                            dot={{ fill: getColor(business.name), strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: 'white', strokeWidth: 2 }}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </GridItem>

              {/* Total Reviews Over Time (Cumulative) */}
              <GridItem colSpan={{ base: 1, lg: 2 }}>
                <Box
                  bg={cardBg}
                  borderRadius="lg"
                  p={6}
                  shadow="lg"
                  border="1px solid"
                  borderColor={borderColor}
                >
                  <HStack spacing={3} mb={6}>
                    <Circle size="40px" bg="blue.100" color="blue.600">
                      <Icon as={FaChartLine} boxSize={4} />
                    </Circle>
                    <VStack align="start" spacing={0}>
                      <Heading size="md" color={textColor}>
                        Total Reviews Over Time
                      </Heading>
                      <Text fontSize="sm" color={subtitleColor}>
                        Cumulative review count trends for selected businesses
                      </Text>
                    </VStack>
                  </HStack>
                  
                  <Box height="400px">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={totalReviewsOverTimeData}
                        margin={{ top: 10, right: 30, left: 0, bottom: 40 }}
                      >
                        <defs>
                          {selectedBusinessesData.map((business) => (
                            <linearGradient
                              key={`total-gradient-${business.name}`}
                              id={`total-color-${business.name.replace(/\s/g, '')}`}
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor={getColor(business.name)}
                                stopOpacity={0.3}
                              />
                              <stop
                                offset="95%"
                                stopColor={getColor(business.name)}
                                stopOpacity={0}
                              />
                            </linearGradient>
                          ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                        <XAxis
                          dataKey="month"
                          tickFormatter={formatMonth}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                          tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                          axisLine={{ stroke: '#E2E8F0' }}
                        />
                        <YAxis
                          tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                          axisLine={{ stroke: '#E2E8F0' }}
                        />
                        <Tooltip
                          labelFormatter={formatMonth}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            padding: '12px 16px',
                          }}
                          formatter={(value, name) => [value.toLocaleString(), name]}
                        />
                        <Legend
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="circle"
                        />
                        {selectedBusinessesData.map((business) => (
                          <Area
                            key={business.name}
                            type="monotone"
                            dataKey={business.name}
                            stroke={getColor(business.name)}
                            strokeWidth={2.5}
                            fill={`url(#total-color-${business.name.replace(/\s/g, '')})`}
                            dot={{ fill: getColor(business.name), strokeWidth: 2, r: 4 }}
                            activeDot={{ r: 6, stroke: 'white', strokeWidth: 2 }}
                          />
                        ))}
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </GridItem>

              {/* Performance Radar */}
              <GridItem>
                <Box
                  bg={cardBg}
                  borderRadius="lg"
                  p={6}
                  shadow="lg"
                  border="1px solid"
                  borderColor={borderColor}
                  height="100%"
                >
                  <HStack spacing={3} mb={6}>
                    <Circle size="40px" bg="purple.100" color="purple.600">
                      <Icon as={FaTrophy} boxSize={4} />
                    </Circle>
                    <VStack align="start" spacing={0}>
                      <Heading size="md" color={textColor}>
                        Performance Overview
                      </Heading>
                      <Text fontSize="sm" color={subtitleColor}>
                        Multi-dimensional comparison
                      </Text>
                    </VStack>
                  </HStack>
                  
                  <Box height="350px">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#E2E8F0" />
                        <PolarAngleAxis
                          dataKey="metric"
                          tick={{ fill: '#4A5568', fontSize: 14, fontWeight: 600 }}
                        />
                        <PolarRadiusAxis
                          angle={30}
                          domain={[0, 100]}
                          tick={{ fill: '#718096', fontSize: 10 }}
                        />
                        {selectedBusinessesData.map((business) => (
                          <Radar
                            key={business.name}
                            name={business.name}
                            dataKey={business.name}
                            stroke={getColor(business.name)}
                            fill={getColor(business.name)}
                            fillOpacity={0.2}
                            strokeWidth={2}
                          />
                        ))}
                        <Legend iconType="circle" />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                          }}
                        />
                      </RadarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </GridItem>

              {/* Sentiment Comparison */}
              <GridItem>
                <Box
                  bg={cardBg}
                  borderRadius="lg"
                  p={6}
                  shadow="lg"
                  border="1px solid"
                  borderColor={borderColor}
                  height="100%"
                >
                  <HStack spacing={3} mb={6}>
                    <Circle size="40px" bg="green.100" color="green.600">
                      <Icon as={FaThumbsUp} boxSize={4} />
                    </Circle>
                    <VStack align="start" spacing={0}>
                      <Heading size="md" color={textColor}>
                        Sentiment Analysis
                      </Heading>
                      <Text fontSize="sm" color={subtitleColor}>
                        Customer satisfaction breakdown
                      </Text>
                    </VStack>
                  </HStack>
                  
                  <Box height="350px">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={sentimentData}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={true} vertical={false} />
                        <XAxis type="number" tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }} />
                        <YAxis
                          dataKey="name"
                          type="category"
                          width={100}
                          tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                        />
                        <Tooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Box
                                  bg="white"
                                  p={4}
                                  boxShadow="xl"
                                  borderRadius="xl"
                                  border="1px solid"
                                  borderColor="gray.100"
                                >
                                  <Text fontWeight="bold" mb={3} fontSize="sm">
                                    {data.fullName}
                                  </Text>
                                  <VStack align="stretch" spacing={2}>
                                    <HStack justify="space-between">
                                      <HStack>
                                        <Circle size="10px" bg={SENTIMENT_COLORS.positive} />
                                        <Text fontSize="sm">Positive</Text>
                                      </HStack>
                                      <Text fontWeight="semibold" fontSize="sm">
                                        {data.positive} ({data.positivePercent}%)
                                      </Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                      <HStack>
                                        <Circle size="10px" bg={SENTIMENT_COLORS.neutral} />
                                        <Text fontSize="sm">Neutral</Text>
                                      </HStack>
                                      <Text fontWeight="semibold" fontSize="sm">
                                        {data.neutral} ({data.neutralPercent}%)
                                      </Text>
                                    </HStack>
                                    <HStack justify="space-between">
                                      <HStack>
                                        <Circle size="10px" bg={SENTIMENT_COLORS.negative} />
                                        <Text fontSize="sm">Negative</Text>
                                      </HStack>
                                      <Text fontWeight="semibold" fontSize="sm">
                                        {data.negative} ({data.negativePercent}%)
                                      </Text>
                                    </HStack>
                                  </VStack>
                                </Box>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend iconType="circle" />
                        <Bar
                          dataKey="positive"
                          stackId="a"
                          fill={SENTIMENT_COLORS.positive}
                          name="Positive"
                          radius={[0, 0, 0, 0]}
                        />
                        <Bar
                          dataKey="neutral"
                          stackId="a"
                          fill={SENTIMENT_COLORS.neutral}
                          name="Neutral"
                        />
                        <Bar
                          dataKey="negative"
                          stackId="a"
                          fill={SENTIMENT_COLORS.negative}
                          name="Negative"
                          radius={[0, 4, 4, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </GridItem>

              {/* SEO Comparison */}
              {seoData.length > 0 && (
                <GridItem>
                  <Box
                    bg={cardBg}
                    borderRadius="lg"
                    p={6}
                    shadow="lg"
                    border="1px solid"
                    borderColor={borderColor}
                  >
                    <HStack spacing={3} mb={6}>
                      <Circle size="40px" bg="orange.100" color="orange.600">
                        <Icon as={FaSearch} boxSize={4} />
                      </Circle>
                      <VStack align="start" spacing={0}>
                        <Heading size="md" color={textColor}>
                          SEO Performance
                        </Heading>
                        <Text fontSize="sm" color={subtitleColor}>
                          Domain strength and organic search metrics
                        </Text>
                      </VStack>
                    </HStack>
                    
                    <Box height="300px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={seoData}
                          margin={{ top: 5, right: 30, left: 20, bottom: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                          <XAxis
                            dataKey="name"
                            tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                            angle={-25}
                            textAnchor="end"
                            height={60}
                          />
                          <YAxis
                            tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                            domain={[0, 100]}
                          />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <Box
                                    bg="white"
                                    p={4}
                                    boxShadow="xl"
                                    borderRadius="xl"
                                    border="1px solid"
                                    borderColor="gray.100"
                                  >
                                    <Text fontWeight="bold" mb={2} fontSize="sm">
                                      {data.fullName}
                                    </Text>
                                    <VStack align="stretch" spacing={1}>
                                      <HStack justify="space-between">
                                        <Text fontSize="sm" color="gray.600">Domain Strength</Text>
                                        <Text fontWeight="bold">{data.domainStrength}</Text>
                                      </HStack>
                                      <HStack justify="space-between">
                                        <Text fontSize="sm" color="gray.600">Organic Clicks</Text>
                                        <Text fontWeight="bold">{data.organicClicks.toLocaleString()}</Text>
                                      </HStack>
                                      <HStack justify="space-between">
                                        <Text fontSize="sm" color="gray.600">Organic Value</Text>
                                        <Text fontWeight="bold">${data.organicValue.toLocaleString()}</Text>
                                      </HStack>
                                    </VStack>
                                  </Box>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="domainStrength" radius={[8, 8, 0, 0]}>
                            {seoData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* SEO Metrics Cards */}
                    <SimpleGrid columns={{ base: 1, md: seoData.length }} spacing={3} mt={4}>
                      {seoData.map((business) => (
                        <Box
                          key={business.fullName}
                          p={4}
                          bg={statBg}
                          borderRadius="xl"
                          borderLeft="4px solid"
                          borderLeftColor={business.fill}
                        >
                          <Text fontWeight="semibold" fontSize="sm" noOfLines={1} mb={2}>
                            {business.fullName}
                          </Text>
                          <HStack justify="space-between">
                            <VStack align="start" spacing={0}>
                              <Text fontSize="xs" color={subtitleColor}>
                                Clicks/mo
                              </Text>
                              <Text fontWeight="bold" fontSize="lg">
                                {business.organicClicks.toLocaleString()}
                              </Text>
                            </VStack>
                            <VStack align="end" spacing={0}>
                              <Text fontSize="xs" color={subtitleColor}>
                                Value
                              </Text>
                              <Text fontWeight="bold" fontSize="lg" color="green.500">
                                ${business.organicValue.toLocaleString()}
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                </GridItem>
              )}

              {/* Website Traffic */}
              {trafficOverTimeData.length > 0 && (
                <GridItem colSpan={seoData.length > 0 ? 1 : { base: 1, lg: 2 }}>
                  <Box
                    bg={cardBg}
                    borderRadius="lg"
                    p={6}
                    shadow="lg"
                    border="1px solid"
                    borderColor={borderColor}
                  >
                    <HStack spacing={3} mb={6}>
                      <Circle size="40px" bg="cyan.100" color="cyan.600">
                        <Icon as={FaGlobe} boxSize={4} />
                      </Circle>
                      <VStack align="start" spacing={0}>
                        <Heading size="md" color={textColor}>
                          Website Traffic Trends
                        </Heading>
                        <Text fontSize="sm" color={subtitleColor}>
                          Organic traffic over the past 12 months
                        </Text>
                      </VStack>
                    </HStack>
                    
                    <Box height="350px">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={trafficOverTimeData}
                          margin={{ top: 5, right: 30, left: 20, bottom: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                          <XAxis
                            dataKey="month"
                            tickFormatter={formatMonth}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                            tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                          />
                          <YAxis
                            tick={{ fill: '#4A5568', fontSize: 13, fontWeight: 600 }}
                            tickFormatter={(value) =>
                              value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value
                            }
                          />
                          <Tooltip
                            labelFormatter={formatMonth}
                            formatter={(value, name) => [
                              value.toLocaleString(),
                              name,
                            ]}
                            contentStyle={{
                              backgroundColor: 'white',
                              border: 'none',
                              borderRadius: '12px',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            }}
                          />
                          <Legend iconType="circle" />
                          {selectedBusinesses.map((businessName) => (
                            <Line
                              key={businessName}
                              type="monotone"
                              dataKey={businessName}
                              stroke={getColor(businessName)}
                              strokeWidth={2.5}
                              dot={{ fill: getColor(businessName), strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6, stroke: 'white', strokeWidth: 2 }}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                </GridItem>
              )}
            </Grid>

            {/* Detailed Comparison Table */}
            <Box
              bg={cardBg}
              borderRadius="lg"
              p={6}
              shadow="lg"
              border="1px solid"
              borderColor={borderColor}
              overflowX="auto"
            >
              <HStack spacing={3} mb={6}>
                <Circle size="40px" bg="gray.100" color="gray.600">
                  <Icon as={FaChartBar} boxSize={4} />
                </Circle>
                <VStack align="start" spacing={0}>
                  <Heading size="md" color={textColor}>
                    Detailed Metrics Comparison
                  </Heading>
                  <Text fontSize="sm" color={subtitleColor}>
                    Complete breakdown of all business metrics
                  </Text>
                </VStack>
              </HStack>

              <Table variant="simple" size="md">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor}>Business</Th>
                    <Th borderColor={borderColor} isNumeric>Total Reviews</Th>
                    <Th borderColor={borderColor} isNumeric>Avg Rating</Th>
                    <Th borderColor={borderColor} isNumeric>Positive %</Th>
                    <Th borderColor={borderColor} isNumeric>Reviews/Month</Th>
                    <Th borderColor={borderColor}>Trend</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {selectedBusinessesData.map((business) => {
                    const sentiment = business.data?.sentimentBreakdown || {
                      positive: 0,
                      neutral: 0,
                      negative: 0,
                    };
                    const total = sentiment.positive + sentiment.neutral + sentiment.negative;
                    const positivePercent = total > 0 ? ((sentiment.positive / total) * 100) : 0;
                    const monthly = Object.values(business.data?.monthlyReviewCounts || {});
                    const avgMonthly = monthly.length > 0
                      ? (monthly.reduce((a, b) => a + b, 0) / monthly.length).toFixed(1)
                      : '0';
                    
                    // Calculate trend (last 3 months vs previous 3)
                    let trendUp = null;
                    if (monthly.length >= 6) {
                      const recent = monthly.slice(-3).reduce((a, b) => a + b, 0);
                      const older = monthly.slice(-6, -3).reduce((a, b) => a + b, 0);
                      trendUp = recent > older;
                    }

                    return (
                      <Tr key={business.name}>
                        <Td borderColor={borderColor}>
                          <HStack spacing={3}>
                            <Circle size="10px" bg={getColor(business.name)} />
                            <Text fontWeight="medium" noOfLines={1} maxW="200px">
                              {business.name}
                            </Text>
                          </HStack>
                        </Td>
                        <Td borderColor={borderColor} isNumeric fontWeight="semibold">
                          {(business.data?.totalReviews || 0).toLocaleString()}
                        </Td>
                        <Td borderColor={borderColor} isNumeric>
                          <HStack justify="flex-end" spacing={1}>
                            <Icon as={FaStar} color="yellow.400" boxSize={3} />
                            <Text fontWeight="semibold">
                              {business.data?.averageRating?.toFixed(1) || 'N/A'}
                            </Text>
                          </HStack>
                        </Td>
                        <Td borderColor={borderColor} isNumeric>
                          <Badge
                            colorScheme={positivePercent >= 80 ? 'green' : positivePercent >= 60 ? 'yellow' : 'red'}
                            borderRadius="full"
                            px={2}
                          >
                            {positivePercent.toFixed(0)}%
                          </Badge>
                        </Td>
                        <Td borderColor={borderColor} isNumeric fontWeight="semibold">
                          {avgMonthly}
                        </Td>
                        <Td borderColor={borderColor}>
                          {trendUp !== null && (
                            <HStack spacing={1}>
                              <Icon
                                as={trendUp ? FaArrowUp : FaArrowDown}
                                color={trendUp ? 'green.500' : 'red.500'}
                                boxSize={3}
                              />
                              <Text
                                fontSize="sm"
                                color={trendUp ? 'green.500' : 'red.500'}
                                fontWeight="medium"
                              >
                                {trendUp ? 'Growing' : 'Declining'}
                              </Text>
                            </HStack>
                          )}
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>

            {/* SWOT Analysis Comparison */}
            {swotComparisonData.length > 0 && (
              <Box
                bg={cardBg}
                borderRadius="lg"
                p={6}
                shadow="lg"
                border="1px solid"
                borderColor={borderColor}
              >
                <HStack spacing={3} mb={6}>
                  <Circle size="40px" bg="purple.100" color="purple.600">
                    <Icon as={FaBalanceScale} boxSize={4} />
                  </Circle>
                  <VStack align="start" spacing={0}>
                    <Heading size="md" color={textColor}>
                      SWOT Analysis Comparison
                    </Heading>
                    <Text fontSize="sm" color={subtitleColor}>
                      Compare what each business does well vs. areas for improvement
                    </Text>
                  </VStack>
                </HStack>

                <VStack spacing={6} align="stretch">
                  {swotComparisonData.map((business) => (
                    <Box
                      key={business.name}
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="xl"
                      overflow="hidden"
                    >
                      {/* Business Header */}
                      <Box
                        bg={business.color}
                        px={4}
                        py={3}
                      >
                        <HStack spacing={3}>
                          <Avatar
                            size="sm"
                            name={business.name}
                            src={business.maps_uri ? businessImages[business.maps_uri] : null}
                            bg="white"
                            color={business.color}
                          />
                          <Text fontWeight="bold" color="white" fontSize="md">
                            {business.name}
                          </Text>
                        </HStack>
                      </Box>

                      {/* Strengths vs Weaknesses Grid */}
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={0}>
                        {/* Strengths Column */}
                        <Box
                          p={5}
                          bg={strengthsBg}
                          borderRightWidth={{ base: 0, md: '1px' }}
                          borderBottomWidth={{ base: '1px', md: 0 }}
                          borderColor={strengthsBorder}
                          minH="200px"
                        >
                          <HStack mb={3} spacing={2}>
                            <Flex
                              w="28px"
                              h="28px"
                              justify="center"
                              align="center"
                              flexShrink={0}
                              bg="green.100"
                              borderRadius="md"
                            >
                              <Icon as={GiStrongMan} color="green.600" boxSize={4} />
                            </Flex>
                            <Text fontWeight="semibold" color={SWOT_COLORS.strengths.heading} fontSize="md">
                              Strengths
                            </Text>
                            <Badge colorScheme="green" fontSize="xs">What they do well</Badge>
                          </HStack>
                          <Box>
                            {business.strengths ? (
                              <ReactMarkdown components={swotMarkdownComponents}>
                                {business.strengths}
                              </ReactMarkdown>
                            ) : (
                              <Text fontSize="sm" color="gray.500" fontStyle="italic">
                                No strengths analysis available
                              </Text>
                            )}
                          </Box>
                        </Box>

                        {/* Weaknesses Column */}
                        <Box
                          p={5}
                          bg={weaknessesBg}
                          borderColor={weaknessesBorder}
                          minH="200px"
                        >
                          <HStack mb={3} spacing={2}>
                            <Flex
                              w="28px"
                              h="28px"
                              justify="center"
                              align="center"
                              flexShrink={0}
                              bg="red.100"
                              borderRadius="md"
                            >
                              <Icon as={MdOutlineSick} color="red.600" boxSize={4} />
                            </Flex>
                            <Text fontWeight="semibold" color={SWOT_COLORS.weaknesses.heading} fontSize="md">
                              Weaknesses
                            </Text>
                            <Badge colorScheme="red" fontSize="xs">Areas to improve</Badge>
                          </HStack>
                          <Box>
                            {business.weaknesses ? (
                              <ReactMarkdown components={swotMarkdownComponents}>
                                {business.weaknesses}
                              </ReactMarkdown>
                            ) : (
                              <Text fontSize="sm" color="gray.500" fontStyle="italic">
                                No weaknesses analysis available
                              </Text>
                            )}
                          </Box>
                        </Box>
                      </SimpleGrid>
                    </Box>
                  ))}
                </VStack>

                {/* Note about SWOT data availability */}
                {swotComparisonData.length < selectedBusinesses.length && (
                  <Box mt={4} p={3} bg={statBg} borderRadius="md">
                    <Text fontSize="sm" color={subtitleColor}>
                      <Icon as={FaBalanceScale} mr={2} />
                      SWOT analysis data is shown for {swotComparisonData.length} of {selectedBusinesses.length} selected businesses. 
                      Other businesses may not have analysis data available yet.
                    </Text>
                  </Box>
                )}
              </Box>
            )}
          </VStack>
        )}
    </Box>
  );
};

export default CompareBusinessDashboard;
